import { Component, ElementRef, forwardRef, Input, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'ndt-segmented-timepicker',
    templateUrl: './segmented-timepicker.component.html',
    styleUrls: ['./segmented-timepicker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SegmentedTimepickerComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class SegmentedTimepickerComponent implements ControlValueAccessor {
   @Input() required = false;
   @Input() label: string;

   @ViewChild('hoursInput') hoursInput!: ElementRef<HTMLInputElement>;
   @ViewChild('minutesInput') minutesInput!: ElementRef<HTMLInputElement>;
   @ViewChild('ampmInput') ampmInput!: ElementRef<HTMLInputElement>;

   hours = '';
   minutes = '';
   ampm = 'AM';

   onChange: any = () => {};
   onTouched: any = () => {};

   isDisabled = false;

   writeValue(value: string | null): void {
      if (!value) {
         this.hours = '';
         this.minutes = '';
         this.ampm = 'AM';
         return;
      }
      const [h, m] = value.split(':');
      let hourNum = parseInt(h, 10);
      this.ampm = hourNum >= 12 ? 'PM' : 'AM';
      if (hourNum === 0) hourNum = 12;
      if (hourNum > 12) hourNum -= 12;
      this.hours = hourNum.toString().padStart(2, '0');
      this.minutes = m.padStart(2, '0');
   }

   registerOnChange(fn: any): void {
      this.onChange = fn;
   }
   registerOnTouched(fn: any): void {
      this.onTouched = fn;
   }

   setDisabledState?(isDisabled: boolean): void {
      this.isDisabled = isDisabled;
      if (this.hoursInput) this.hoursInput.nativeElement.disabled = isDisabled;
      if (this.minutesInput) this.minutesInput.nativeElement.disabled = isDisabled;
      if (this.ampmInput) this.ampmInput.nativeElement.disabled = isDisabled;
   }

   updateValue() {
      if (!this.hours && !this.minutes) {
         this.onChange(null);
         return;
      }

      let hourNum = parseInt(this.hours || '0', 10);
      let minNum = parseInt(this.minutes || '0', 10);

      if (this.ampm === 'PM' && hourNum < 12) hourNum += 12;
      if (this.ampm === 'AM' && hourNum === 12) hourNum = 0;

      const formatted = `${hourNum.toString().padStart(2, '0')}:${minNum.toString().padStart(2, '0')}`;
      this.onChange(formatted);
   }

   onHoursInput(value: string) {
      this.hours = value.replace(/\D/g, '').slice(0, 2);
      if (this.hours.length === 2) {
         this.minutesInput.nativeElement.focus();
         this.minutesInput.nativeElement.select();
      }
      this.updateValue();
   }

   onMinutesInput(value: string) {
      this.minutes = value.replace(/\D/g, '').slice(0, 2);
      if (this.minutes.length === 2) {
         this.ampmInput.nativeElement.focus();
         this.ampmInput.nativeElement.select();
      }
      this.updateValue();
   }

   onAmPmInput(value: string) {
      const firstChar = value[0]?.toUpperCase();
      if (firstChar === 'A') this.ampm = 'AM';
      else if (firstChar === 'P') this.ampm = 'PM';
      this.updateValue();
   }

   toggleAmPm() {
      this.ampm = this.ampm === 'AM' ? 'PM' : 'AM';
      this.updateValue();
   }

   selectAll(input: HTMLInputElement) {
      setTimeout(() => input.select(), 0);
   }

   parseShorthandOnBlur() {
      if (this.hours) {
         let h = parseInt(this.hours.replace(/\D/g, ''), 10);
         h = Math.max(1, Math.min(12, h));
         this.hours = h.toString().padStart(2, '0');
      }

      if (this.minutes) {
         let m = parseInt(this.minutes.replace(/\D/g, ''), 10);
         m = Math.max(0, Math.min(59, m));
         this.minutes = m.toString().padStart(2, '0');
      }

      if (this.ampm) {
         const firstChar = this.ampm[0].toUpperCase();
         if (firstChar === 'A') this.ampm = 'AM';
         else if (firstChar === 'P') this.ampm = 'PM';
      }

      this.updateValue();
   }

   onArrowKey(event: KeyboardEvent, field: 'hours' | 'minutes' | 'ampm') {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;

      event.preventDefault();
      if (field === 'hours') {
         let h = parseInt(this.hours || '0', 10);
         h = event.key === 'ArrowUp' ? h + 1 : h - 1;
         if (h < 1) h = 12;
         if (h > 12) h = 1;
         this.hours = h.toString().padStart(2, '0');
      } else if (field === 'minutes') {
         let m = parseInt(this.minutes || '0', 10);
         m = event.key === 'ArrowUp' ? m + 1 : m - 1;
         if (m < 0) m = 59;
         if (m > 59) m = 0;
         this.minutes = m.toString().padStart(2, '0');
      } else if (field === 'ampm') {
         this.ampm = this.ampm === 'AM' ? 'PM' : 'AM';
      }
      this.updateValue();
   }
}
